import React, { memo, useCallback, useMemo, useRef } from 'react';

import { createUseStyles } from '@Sage/hoi-poi-ui';
import { downloadFile } from '@Sage/ssm-utils';

import BoxInfo from '../BoxInfo';
import { convertTableToCSV } from '../../../hooks/dom-utils';
import { removeTags } from '../../../hooks/utils';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotTable');

// Move static styles outside component to avoid recreation
const tableStyles = {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'auto',
};

// Custom comparison function for memo
const areEqual = (prevProps, nextProps) => {
    return (
        prevProps.id === nextProps.id &&
        prevProps.loading === nextProps.loading &&
        prevProps.data === nextProps.data
    );
};

const CopilotTable = memo(({ id, loading, data }) => {
    const tableRef = useRef(null);

    const classes = useStyles();

    const handleDownload = useCallback(() => {
        const table = tableRef.current;
        if (table) {
            const csvData = convertTableToCSV(table, removeTags);
            downloadFile(csvData, `table_${id}.csv`, 'text/csv');
        }
    }, [id]);

    const boxInfoProps = useMemo(
        () => ({
            isTable: true,
            onDownload: handleDownload,
            loading: loading,
        }),
        [handleDownload, loading],
    );

    // Memoize headers calculation
    const headers = useMemo(() => {
        if (!data?.length) return [];
        return Object.keys(data[0]);
    }, [data]);

    // Memoize column width calculations
    const columnMaxWidths = useMemo(() => {
        if (!data?.length || !headers.length) return {};

        return headers.reduce((acc, header) => {
            acc[header] =
                Math.max(header.length, ...data.map((row) => row[header]?.toString().length || 0)) *
                20;
            return acc;
        }, {});
    }, [data, headers]);

    // Memoize table rows to prevent recreation on every render
    const tableRows = useMemo(() => {
        if (!data?.length) return null;

        return data.map((rowData, rowIndex) => (
            <tr key={rowIndex}>
                {headers.map((header) => (
                    <td
                        key={header}
                        className={classes.rowCell}
                        style={{ width: `${columnMaxWidths[header]}px` }}
                    >
                        {rowData[header] || ''}
                    </td>
                ))}
            </tr>
        ));
    }, [data, headers, columnMaxWidths, classes.rowCell]);

    // Memoize table headers to prevent recreation
    const tableHeaders = useMemo(() => {
        if (!headers.length) return null;

        return headers.map((headerText) => (
            <th
                key={headerText}
                className={classes.headerCell}
                style={{ width: `${columnMaxWidths[headerText]}px` }}
            >
                {headerText}
            </th>
        ));
    }, [headers, columnMaxWidths, classes.headerCell]);

    if (!data?.length) {
        return null;
    }

    return (
        <BoxInfo {...boxInfoProps}>
            <table className={classes.root} style={tableStyles} ref={tableRef}>
                <thead>
                    <tr>{tableHeaders}</tr>
                </thead>
                <tbody>{tableRows}</tbody>
            </table>
        </BoxInfo>
    );
}, areEqual);

export default CopilotTable;
